import { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  MenuItem,
  Chip,
  Checkbox,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import { useFetchVales, useCreateVale, useUpdateVale, useDeleteVale } from '@/shared/actions/erp/comisiones/comisiones.actions';
import { useFetchCitiesEnabled } from '@/shared/actions/erp';
import LoadingDialog from './LoadingDialog';
import type { ValeRegla } from '@/lib/commissions/types';
import { VALES_PREDEFINIDOS } from '@/lib/commissions/constants';

interface ValesFormProps {
  onSaved: () => void;
}

type ValeFormState = Omit<ValeRegla, 'ciudad'> & { ciudad: string[] };

const buildValeFormState = (vale?: ValeRegla): ValeFormState => ({
  id: vale?.id,
  rol: vale?.rol ?? 'vendedor',
  tipo: vale?.tipo ?? 'alimentacion',
  umbral: vale?.umbral ?? 5,
  monto: vale?.monto ?? 50,
  ciudad:
    vale?.ciudad && vale.ciudad !== 'null'
      ? vale.ciudad.split(',').map((c) => c.trim().toUpperCase())
      : [],
});

export default function ValesForm({ onSaved }: ValesFormProps) {
  const { data: vales, isLoading } = useFetchVales();
  const createMutation = useCreateVale({ enableSuccessAlert: true });
  const updateMutation = useUpdateVale({ enableSuccessAlert: true });
  const deleteMutation = useDeleteVale();

  const { data: citiesResponse, isLoading: isLoadingCities } = useFetchCitiesEnabled({
    params: { page_size: 1000, estado: true }
  });

  const ciudades = citiesResponse?.data?.data || [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVale, setEditingVale] = useState<ValeFormState | null>(null);
  const [searchText, setSearchText] = useState('');
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const updateEditingVale = (updates: Partial<ValeFormState>) => {
    setEditingVale((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const handleCiudadChange = (event: SelectChangeEvent<string[]>) => {
    const rawValue = event.target.value;
    setEditingVale((prev) => {
      if (!prev) return prev;
      const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;

      if (value.includes('TODAS')) {
        return {
          ...prev,
          ciudad: prev.ciudad.includes('TODAS') ? [] : ['TODAS'],
        };
      }

      return {
        ...prev,
        ciudad: value.filter((v) => v !== 'TODAS'),
      };
    });
  };

  const handleOpenDialog = (vale?: ValeRegla) => {
    setEditingVale(buildValeFormState(vale));
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingVale(null);
    setSearchText('');
  };

  const handleSave = async () => {
    if (!editingVale) return;

    try {
      const { ciudad, ...rest } = editingVale;
      const valeData: Partial<ValeRegla> = {
        ...rest,
        ciudad: ciudad.length === 0 || ciudad.includes('TODAS') ? null : ciudad.join(','),
      };

      if (editingVale.id) {
        await updateMutation.mutateAsync(valeData as ValeRegla);
      } else {
        await createMutation.mutateAsync(valeData);
      }
      onSaved();
      handleCloseDialog();
    } catch (error) {
      // Error silencioso en producción
    }
  };

  const handleDelete = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este vale?')) {
      await deleteMutation.mutateAsync(id);
      onSaved();
    }
  };

  const handleRestablecer = async () => {
    if (!confirm('¿Estás seguro de restablecer todos los vales a los valores predefinidos? Esto eliminará todos los vales existentes y creará nuevos con los valores predeterminados.')) {
      return;
    }

    try {
      setLoadingDialogOpen(true);
      setLoadingProgress(0);

      const totalSteps = (vales?.length || 0) + VALES_PREDEFINIDOS.length;
      let currentStep = 0;

      if (vales && vales.length > 0) {
        for (const vale of vales) {
          if (vale.id) {
            await deleteMutation.mutateAsync(vale.id);
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
          }
        }
      }

      for (const vale of VALES_PREDEFINIDOS) {
        await createMutation.mutateAsync(vale);
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }

      onSaved();
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    } catch (error) {
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    }
  };

  const filteredCiudades = useMemo(() => {
    const uniqueCiudades = ciudades.reduce((acc: any[], ciudad: any) => {
      const nombreNormalizado = ciudad.nombre.toLowerCase();
      if (!acc.some(c => c.nombre.toLowerCase() === nombreNormalizado)) {
        acc.push(ciudad);
      }
      return acc;
    }, []);

    if (!searchText) return uniqueCiudades;
    const searchLower = searchText.toLowerCase();
    return uniqueCiudades.filter((ciudad: any) =>
      ciudad.nombre.toLowerCase().includes(searchLower)
    );
  }, [ciudades, searchText]);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Vales (Alimentación/Movilización)</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleRestablecer}
            size="small"
            color="warning"
            disabled={createMutation.isPending || deleteMutation.isPending}
          >
            Restablecer
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nuevo Vale
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rol</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell align="right">Umbral</TableCell>
              <TableCell align="right">Monto</TableCell>
              <TableCell>Ciudad</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vales?.map((vale) => (
              <TableRow key={vale.id}>
                <TableCell>{vale.rol}</TableCell>
                <TableCell>
                  <Chip
                    label={vale.tipo === 'alimentacion' ? 'Alimentación' : 'Movilización'}
                    color={vale.tipo === 'alimentacion' ? 'success' : 'info'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">{vale.umbral}</TableCell>
                <TableCell align="right">${vale.monto.toFixed(2)}</TableCell>
                <TableCell>
                  {!vale.ciudad || vale.ciudad === 'null'
                    ? 'Todas'
                    : vale.ciudad === 'TODAS'
                      ? 'Todas'
                      : vale.ciudad.split(',').map(c => c.trim().charAt(0).toUpperCase() + c.trim().slice(1).toLowerCase()).join(', ')}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(vale)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => vale.id && handleDelete(vale.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de edición */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVale?.id ? 'Editar Vale' : 'Nuevo Vale'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Rol"
              value={editingVale?.rol || 'vendedor'}
              onChange={(e) => updateEditingVale({ rol: e.target.value })}
              fullWidth
              required
            >
              <MenuItem value="asesor">Asesor</MenuItem>
              <MenuItem value="vendedor">Vendedor</MenuItem>
              <MenuItem value="supervisor">Supervisor</MenuItem>
            </TextField>
            <TextField
              select
              label="Tipo"
              value={editingVale?.tipo || 'alimentacion'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'alimentacion' || value === 'movilizacion') {
                  updateEditingVale({ tipo: value });
                }
              }}
              fullWidth
              required
            >
              <MenuItem value="alimentacion">Alimentación</MenuItem>
              <MenuItem value="movilizacion">Movilización</MenuItem>
            </TextField>
            <TextField
              label="Umbral"
              type="number"
              value={editingVale?.umbral || 5}
              onChange={(e) => updateEditingVale({ umbral: parseInt(e.target.value, 10) })}
              fullWidth
              required
              inputProps={{ min: 0 }}
              helperText="Número mínimo de ventas para obtener este vale"
            />
            <TextField
              label="Monto"
              type="number"
              value={editingVale?.monto || 50}
              onChange={(e) => updateEditingVale({ monto: parseFloat(e.target.value) })}
              fullWidth
              required
              inputProps={{ step: 0.01, min: 0 }}
              helperText="Monto del vale en dólares"
            />
            <FormControl fullWidth>
              <InputLabel id="ciudad-select-label">Ciudad</InputLabel>
              <Select
                labelId="ciudad-select-label"
                multiple
                value={editingVale?.ciudad ?? []}
                onChange={handleCiudadChange}
                onClose={() => setSearchText('')}
                input={<OutlinedInput label="Ciudad" />}
                renderValue={(selected: string[]) => {
                  if (selected.length === 0) return 'Ninguna seleccionada';
                  if (selected.includes('TODAS')) return 'Todas las ciudades';
                  return selected.join(', ');
                }}
                disabled={isLoadingCities}
                MenuProps={{
                  autoFocus: false,
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        py: 0.5,
                      }
                    }
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Buscar ciudad..."
                    fullWidth
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </Box>
                <MenuItem value="TODAS">
                  <Checkbox checked={(editingVale?.ciudad ?? []).includes('TODAS')} />
                  <ListItemText primary="Todas las ciudades" />
                </MenuItem>
                {filteredCiudades.map((ciudad: any) => {
                  const nombreNormalizado = ciudad.nombre.charAt(0).toUpperCase() + ciudad.nombre.slice(1).toLowerCase();
                  const ciudadMayusculas = ciudad.nombre.toUpperCase();
                  return (
                    <MenuItem
                      key={`vale-ciudad-${ciudad.id}`}
                      value={ciudadMayusculas}
                      disabled={(editingVale?.ciudad ?? []).includes('TODAS')}
                    >
                      <Checkbox checked={(editingVale?.ciudad ?? []).includes(ciudadMayusculas)} />
                      <ListItemText primary={nombreNormalizado} />
                    </MenuItem>
                  );
                })}
                {filteredCiudades.length === 0 && (
                  <MenuItem disabled>
                    <ListItemText primary="No se encontraron ciudades" />
                  </MenuItem>
                )}
              </Select>
              <Typography variant="caption" sx={{ mt: 0.5, ml: 1.75, color: 'text.secondary' }}>
                Selecciona "Todas" o una o varias ciudades específicas
              </Typography>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de carga */}
      <LoadingDialog
        open={loadingDialogOpen}
        progress={loadingProgress}
        title="Restableciendo vales..."
        message="Eliminando vales actuales y creando los valores predefinidos."
      />
    </Box>
  );
}

